import { useNavigate } from '@tanstack/react-router'
import { useRef } from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from '~/components/icons'
import { todayKey } from '~/data/weighins'
import { shifted } from '~/lib/day-step'
import { formatDayLabel, dayStart } from '~/lib/dates'
import { DIALOG_PATHS } from '~/lib/dialogs'

/**
 * The day a weigh-in panel is on, at its top: today until told otherwise, stepped a day at a
 * time with the arrows, or picked outright — the date is a control, and pressing it opens
 * the browser's own calendar. Tomorrow is out of reach; a weigh-in is a record, not a plan.
 *
 * Moving is a navigation on the panel's own address, so a form holding unsaved typing gets
 * to ask before the day is pulled out from under it.
 */
export function WeighInDayRow({ day, dialog }: { day: string; dialog: 'weighin-weight' }) {
  const navigate = useNavigate()
  const today = todayKey()

  const go = (next: string) => {
    void navigate({
      to: '.',
      search: ({ day: _left, ...prev }) => ({
        ...prev,
        dialog,
        ...(next === today ? {} : { day: next }),
      }),
      mask: { to: DIALOG_PATHS[dialog] },
      replace: true,
      resetScroll: false,
      viewTransition: false,
    })
  }

  return <DayRow day={day} max={today} onGo={go} />
}

/**
 * The row itself, for anything that is on a day: an arrow at each edge and the day between
 * them, the date a control that opens the browser's own calendar. [max] is the last day that
 * can be reached, where there is one — a weigh-in is a record and stops at today.
 *
 * The width, taken: bunched into the corner the three read as a control to aim at; spread
 * across the panel they read as what the panel is currently about, with the way either side of
 * it where the pointer already is.
 */
export function DayRow({
  day,
  max = null,
  onGo,
}: {
  day: string
  max?: string | null
  onGo: (day: string) => void
}) {
  const picker = useRef<HTMLInputElement>(null)

  const go = (next: string) => {
    if (max !== null && next > max) {
      return
    }
    onGo(next)
  }

  return (
    <div className="flex items-center justify-between px-4 pb-2">
      <button
        type="button"
        aria-label="The day before"
        className="rounded-full p-1 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
        onClick={() => {
          go(shifted(day, -1))
        }}
      >
        <ChevronLeftIcon aria-hidden className="size-5" />
      </button>
      <span className="relative">
        <button
          type="button"
          className="rounded-lg px-2 py-0.5 text-sm font-medium transition-colors hover:bg-hover"
          onClick={() => {
            const input = picker.current
            if (input === null) {
              return
            }
            try {
              if (typeof input.showPicker === 'function') {
                input.showPicker()
              } else {
                input.click()
              }
            } catch {
              input.click()
            }
          }}
        >
          {formatDayLabel(dayStart(day))}
        </button>
        {/* The browser's own calendar, anchored under the date it opens from. */}
        <input
          ref={picker}
          type="date"
          aria-label="Pick a day"
          tabIndex={-1}
          {...(max === null ? {} : { max })}
          value={day}
          onChange={(event) => {
            const next = event.target.value
            if (/^\d{4}-\d{2}-\d{2}$/.test(next)) {
              go(next)
            }
          }}
          className="pointer-events-none absolute inset-0 opacity-0"
        />
      </span>
      <button
        type="button"
        aria-label="The day after"
        disabled={max !== null && day >= max}
        className="rounded-full p-1 text-ink-muted transition-colors hover:bg-hover hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
        onClick={() => {
          go(shifted(day, 1))
        }}
      >
        <ChevronRightIcon aria-hidden className="size-5" />
      </button>
    </div>
  )
}
